import { apiClient } from '@/core/api/client';
import { Endpoints } from '@/core/api/endpoints';
import { logger } from '@/core/logger';
import { shouldInvokeSkipPasscodeApi } from '@/modules/passcode/utils/skipPasscodeGate';
/**
 * API response types for passcode operations
 */
export interface SetPasscodeRequest {
    passcode: string;
    biometric_enabled: boolean;
    device_id: string;
}

export interface SetPasscodeResponse {
    status: 'success';
    message: string;
}

export interface VerifyPasscodeRequest {
    passcode: string;
    device_id: string;
}

export interface VerifyPasscodeResponse {
    status: 'success';
    message: string;
    accessToken?: string;
    refreshToken?: string;
}

export interface ChangePasscodeRequest {
    current_passcode: string;
    new_passcode: string;
}

export interface ChangePasscodeResponse {
    status: 'success';
    message: string;
}

export interface SkipPasscodeRequest {
    device_id: string;
    from: string;
    is_skip: boolean;
}

export interface SkipPasscodeResponse {
    status: 'success';
    message: string;
    accessToken?: string;
}

export interface ForgotPasscodeResponse {
    status: 'success';
    message: string;
}

/**
 * Passcode service for API operations
 * Handles all passcode-related API calls
 */
class PasscodeService {
    /**
     * Set passcode for the user
     */
    async setPasscode(
        passcode: string,
        biometricEnabled: boolean,
        deviceId: string
    ): Promise<SetPasscodeResponse> {
        return await apiClient.post<SetPasscodeResponse, SetPasscodeRequest>(
            Endpoints.profile.setPasscode,
            {
                passcode,
                biometric_enabled: biometricEnabled,
                device_id: deviceId,
            }
        );
    }

    /**
     * Verify passcode
     */
    async verifyPasscode(
        passcode: string,
        deviceId: string
    ): Promise<VerifyPasscodeResponse> {
        return await apiClient.post<VerifyPasscodeResponse, VerifyPasscodeRequest>(
            Endpoints.profile.verifyPasscode,
            {
                passcode,
                device_id: deviceId,
            }
        );
    }

    /**
     * Change passcode
     */
    async changePasscode(
        currentPasscode: string,
        newPasscode: string
    ): Promise<ChangePasscodeResponse> {
        return await apiClient.post<ChangePasscodeResponse, ChangePasscodeRequest>(
            Endpoints.profile.changePasscode,
            {
                current_passcode: currentPasscode,
                new_passcode: newPasscode,
            }
        );
    }

    /**
     * Skip passcode setup — email/password login only.
     * UAE Pass, Emirates ID, and ADHA catalogue sessions must never call this API.
     */
    async skipPasscode(deviceId: string, from: string = 'login'): Promise<SkipPasscodeResponse> {
        if (!shouldInvokeSkipPasscodeApi()) {
            logger.info('PasscodeService:skipPasscode skipped — not email/password login', {
                from,
            });
            return {
                status: 'success',
                message: 'Skipped — not email/password login',
            };
        }

        return await apiClient.post<SkipPasscodeResponse, SkipPasscodeRequest>(
            Endpoints.profile.skipPasscode,
            {
                device_id: deviceId,
                from,
                is_skip: true,
            }
        );
    }

    /**
     * Reset passcode (forgot passcode)
     * Resets passcode flags on backend
     */
    async forgotPasscode(): Promise<ForgotPasscodeResponse> {
        return await apiClient.post<ForgotPasscodeResponse, {}>(
            Endpoints.profile.forgotPasscode,
            {}
        );
    }
}

export const passcodeService = new PasscodeService();

